import { Controller, useFormContext } from 'react-hook-form';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field';
import { ComboboxField } from 'components/DesignSystems/ComboboxField/ComboboxField';
import {
  notificationPriorityOptions,
  notificationTypeOptions,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/NewNotificationDrawer';
import { NotificationType } from 'hooks/useNotification';
import { useRosters } from 'hooks/useRosters';
import { selectGroupGuid, useAppSelector } from 'slices/commonSelectors';
import EventChannelMessageInput from './EventChannelMessageInput';
import type { EventNotificationFormValues } from './eventNotification';

// Duplicates the fields in NotificationsTab/NewNotificationDrawer, rebuilt with shadcn.
// TODO: Extract a shared notification fields component when the existing
// notifications UI is migrated to shadcn.
const EventNotificationForm: React.FC = () => {
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<EventNotificationFormValues>();
  const groupGuid = useAppSelector(selectGroupGuid);
  const { data: rosters } = useRosters([groupGuid]);

  const recipientOptions =
    rosters?.map((user) => ({
      label: user.email_address,
      value: user.user_guid,
    })) ?? [];

  const handleChannelChange = (channels: NotificationType[]) => {
    if (!channels.includes(NotificationType.EMAIL)) {
      setValue('emailSubject', '');
      setValue('emailMessage', '');
    }
    if (!channels.includes(NotificationType.TEAMS)) {
      setValue('teamsMessage', '');
    }
    if (!channels.includes(NotificationType.SMS)) {
      setValue('smsMessage', '');
    }
  };

  return (
    <>
      <p className="mb-3">
        This notification is sent once, when the event is created.
      </p>
      <Controller
        control={control}
        name="notificationTypes"
        render={({ field }) => (
          <FieldSet className="mb-3" data-invalid={!!errors.notificationTypes}>
            <FieldLegend variant="label">Communication Channels</FieldLegend>
            <FieldGroup data-slot="checkbox-group">
              {notificationTypeOptions.map((option) => {
                const id = `eventNotificationChannel-${option.value}`;
                return (
                  <Field key={option.value} orientation="horizontal">
                    <Checkbox
                      id={id}
                      checked={field.value.includes(option.value)}
                      disabled={option.disabled}
                      onCheckedChange={(checked) => {
                        const channels = checked
                          ? [...field.value, option.value]
                          : field.value.filter(
                              (channel) => channel !== option.value
                            );
                        field.onChange(channels);
                        handleChannelChange(channels);
                      }}
                    />
                    <FieldLabel
                      htmlFor={id}
                      className="tw:font-normal tw:capitalize"
                    >
                      {option.label}
                    </FieldLabel>
                  </Field>
                );
              })}
            </FieldGroup>
            <FieldError errors={[errors.notificationTypes]} />
          </FieldSet>
        )}
      />
      <div className="mb-3">
        <EventChannelMessageInput />
      </div>
      <div className="mb-3">
        <Controller
          control={control}
          name="acknowledgementRequired"
          render={({ field }) => (
            <Field orientation="horizontal">
              <Checkbox
                id="eventNotificationAcknowledgementRequired"
                checked={!!field.value}
                onCheckedChange={(checked) => field.onChange(checked)}
              />
              <FieldLabel
                htmlFor="eventNotificationAcknowledgementRequired"
                className="tw:font-normal"
              >
                Let Recipients Acknowledge and Dismiss
              </FieldLabel>
            </Field>
          )}
        />
      </div>
      <Controller
        control={control}
        name="userIds"
        render={({ field }) => (
          <Field className="mb-3" data-invalid={!!errors.userIds}>
            <ComboboxField
              label="Recipients *"
              multiple
              options={recipientOptions}
              value={field.value}
              onChange={field.onChange}
              placeholder="Select recipients"
              emptyMessage="No users found."
            />
            <FieldError errors={[errors.userIds]} />
          </Field>
        )}
      />
      <Controller
        control={control}
        name="priority"
        render={({ field }) => (
          <Field data-invalid={!!errors.priority}>
            <ComboboxField
              label="Severity *"
              options={notificationPriorityOptions}
              value={field.value}
              onChange={field.onChange}
            />
            <FieldError errors={[errors.priority]} />
          </Field>
        )}
      />
    </>
  );
};

export default EventNotificationForm;
